import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { ClosedTimesheetApprovalTableRow } from './useAllOPLogsMapped';
import { Typography } from '../../../components/Typography/Typography';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { Button, Chip, useTheme } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { SelectItem, TimesheetStatus } from '../../../types';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  approvalDetailsModalState,
  tsApprovalsSelectedOYearFilterState,
} from '../../../state/UIAccountantState';
import { ROUTES } from '../../../constants/routes';
import { useNavigate } from 'react-router';
import { getAccountantTsStatusLabel } from '../../../utils/tsMappers';

const columnHelper = createColumnHelper<ClosedTimesheetApprovalTableRow>();

export const useClosedTSTableColumns = () => {
  const { colors } = useTheme();
  const setApprovalDetailsModal = useSetAtom(approvalDetailsModalState);
  const navigate = useNavigate();
  const selectedYear = useAtomValue(tsApprovalsSelectedOYearFilterState);

  const handleApprovalDetailsModal = useCallback(
    (userId: number, tsId: number) => {
      setApprovalDetailsModal({
        userId,
        tsId,
      });
    },
    [setApprovalDetailsModal]
  );

  const redirectToSingleTs = useCallback(
    (tsId: number) => {
      navigate(`/${ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}/${tsId}?year=${selectedYear}`, {
        state: { from: ROUTES.TIMESHEET_APPROVALS },
      });
    },
    [navigate, selectedYear]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('timesheetName', {
        cell: (info) => (
          <Button
            variant='text'
            onClick={() => redirectToSingleTs(info.row.original.tsId)}
            style={{
              fontSize: '16px',
            }}
          >
            {info.getValue()}
          </Button>
        ),
        header: () => <TableHeaderCell text='Timesheet Name' />,
        meta: {
          width: '330px',
          minWidth: '330px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
        filterFn: (row, id, filterValue: SelectItem[]) => {
          if (!filterValue.length) return true;
          return filterValue.some((user) => row.original.timesheetName.includes(user.value));
        },
      }),
      columnHelper.accessor('status', {
        cell: (info) => (
          <Chip
            style={{
              backgroundColor: colors.surfaceStatus.noAction,
              minWidth: '66px',
              height: '28px',
            }}
            label={
              <Typography variant='body' color={colors.text.main}>
                {getAccountantTsStatusLabel(info.getValue() as TimesheetStatus)}
              </Typography>
            }
          />
        ),
        header: () => <TableHeaderCell text='Status' />,
        meta: {
          width: '147px',
          maxWidth: '147px',
          minWidth: '147px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('submittedOn', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main}>
            {info.getValue() ? format(parseISO(info.getValue() ?? ''), 'MMM d, yyyy') : '-'}
          </Typography>
        ),
        header: () => <TableHeaderCell text='Submitted On' />,
        meta: {
          width: '211px',
          maxWidth: '211px',
          minWidth: '211px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('closedOn', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main}>
            {info.getValue() ? format(parseISO(info.getValue() ?? ''), 'MMM d, yyyy') : '-'}
          </Typography>
        ),
        header: () => <TableHeaderCell text='Closed On' />,
        meta: {
          width: '211px',
          maxWidth: '211px',
          minWidth: '211px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('duration', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main} style={{ textAlign: 'right' }}>
            {info.getValue()}
          </Typography>
        ),
        header: () => (
          <TableHeaderCell
            text='Duration (Days)'
            style={{
              textAlign: 'right',
            }}
          />
        ),
        meta: {
          width: '211px',
          maxWidth: '211px',
          minWidth: '211px',
          thStyles: { paddingLeft: '16px', textAlign: 'right' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('cycle', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main} style={{ textAlign: 'right' }}>
            {info.getValue()}
          </Typography>
        ),
        header: () => (
          <TableHeaderCell
            text='Cycle'
            style={{
              textAlign: 'right',
            }}
          />
        ),
        meta: {
          width: '211px',
          maxWidth: '211px',
          minWidth: '211px',
          thStyles: { paddingLeft: '16px', textAlign: 'right' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => (
          <Button
            variant='outlined'
            onClick={() => {
              handleApprovalDetailsModal(info.row.original.userId, info.row.original.tsId);
            }}
            style={{
              backgroundColor: 'transparent',
              height: '28px',
            }}
          >
            <Typography
              variant='body'
              color={colors.textAccent.default}
              style={{ marginBottom: '-1px' }}
            >
              Review
            </Typography>
          </Button>
        ),
        header: () => <TableHeaderCell text='Action' />,
        meta: {
          width: '97px',
          maxWidth: '97px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
    ],
    [
      colors.surfaceStatus.noAction,
      colors.text.main,
      colors.textAccent.default,
      handleApprovalDetailsModal,
      redirectToSingleTs,
    ]
  );
  return columns;
};
